import React, { useEffect, useRef, useState } from 'react';

// One "Language" button for the story players: what you hear (the story's
// own audio + dubs), subtitles, and asking Pip to record a new dub.
//
// The pill itself tells you what's going on:
//   - white:   the story's own audio ("EN English")
//   - blue:    listening to a dub ("ES Español")
//   - making:  a ring fills around the badge while your dub records (~1 min)
//   - ready:   a little pop, "Now in Deutsch!", for a few seconds
// Subtitles on adds a small "CC" tag.
//
// Renders nothing when there's neither audio to pick nor subtitles.

const MAKE_SECONDS = 70; // a dub usually takes about a minute
const READY_FLASH_MS = 4500;

export const languageName = (code) => {
  if (!code) return '';
  try {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : code;
  } catch (_) {
    return code;
  }
};

const badgeText = (code) => (code ? code.toUpperCase().slice(0, 3) : '♪');

const css = `
.pxl-pill { transition: transform 160ms ease, background 200ms ease, color 200ms ease; }
.pxl-pill:hover { transform: translateY(-1px); }
.pxl-pill:active { transform: scale(0.96); }
.pxl-pill:focus-visible { outline: 3px solid #0065F8; outline-offset: 2px; }
@keyframes pxlPop { 0% { transform: scale(1); } 35% { transform: scale(1.14); } 65% { transform: scale(0.97); } 100% { transform: scale(1); } }
.pxl-pop { animation: pxlPop 520ms cubic-bezier(0.34,1.56,0.64,1); }
@keyframes pxlSpark { 0% { opacity: 0; transform: translate(0,0) scale(0.4); } 30% { opacity: 1; } 100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1); } }
.pxl-spark { position: absolute; left: 50%; top: 50%; width: 7px; height: 7px; margin: -3.5px; border-radius: 50%; animation: pxlSpark 900ms ease-out both; pointer-events: none; }
@keyframes pxlIn { from { opacity: 0; transform: translateY(8px) scale(0.97); } to { opacity: 1; transform: none; } }
.pxl-menu { animation: pxlIn 200ms cubic-bezier(0.22,1,0.36,1) both; }
.pxl-row { transition: background 120ms ease; }
.pxl-row:not(:disabled):hover { background: #F1F6FF !important; }
.pxl-row:focus-visible { outline: 3px solid #0065F8; outline-offset: -3px; }
@keyframes pxlSpin { to { transform: rotate(360deg); } }
.pxl-spin { animation: pxlSpin 900ms linear infinite; }
@media (prefers-reduced-motion: reduce) { .pxl-pop, .pxl-spark, .pxl-menu { animation: none; } .pxl-spin { animation-duration: 2.4s; } }
`;

const SPARKS = [
  ['#FFC93C', '-26px', '-18px'],
  ['#FF6B9A', '24px', '-20px'],
  ['#3DD68C', '30px', '10px'],
  ['#0065F8', '-30px', '12px'],
  ['#FFC93C', '0px', '-28px'],
];

function Badge({ code, active, progress }) {
  const size = 24;
  return (
    <span style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'inline-flex' }}>
      {progress != null && (
        <svg width={size + 8} height={size + 8} viewBox="0 0 32 32" style={{ position: 'absolute', left: -4, top: -4, transform: 'rotate(-90deg)' }} aria-hidden="true">
          <circle cx="16" cy="16" r="14" fill="none" stroke="rgba(0,101,248,0.18)" strokeWidth="3" />
          <circle
            cx="16"
            cy="16"
            r="14"
            fill="none"
            stroke="#0065F8"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${Math.max(4, progress * 88)} 88`}
            style={{ transition: 'stroke-dasharray 900ms linear' }}
          />
        </svg>
      )}
      <span
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: code && code.length > 2 ? 8.5 : 10,
          fontWeight: 700,
          letterSpacing: '0.02em',
          background: active ? '#FFFFFF' : '#EAF3FF',
          color: '#0065F8',
        }}
      >
        {badgeText(code)}
      </span>
    </span>
  );
}

function Row({ onClick, disabled, selected, left, right, title }) {
  return (
    <button
      type="button"
      role={selected === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={selected}
      className="pxl-row"
      disabled={disabled}
      title={title}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        width: '100%',
        minHeight: 42,
        padding: '0 12px',
        border: 'none',
        borderRadius: 12,
        background: 'transparent',
        color: disabled ? 'rgba(11,26,51,0.4)' : '#0B1A33',
        fontFamily: "'Fredoka', 'Inter', sans-serif",
        fontSize: 15,
        fontWeight: selected ? 700 : 600,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>{left}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, color: selected ? '#0065F8' : '#5A6B85', fontSize: 14, fontWeight: 600 }}>{right}</span>
    </button>
  );
}

const Chevron = ({ back = false }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={back ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
  </svg>
);

const SpeakerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0065F8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5L6 9H3v6h3l5 4V5z" />
    <path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13" />
  </svg>
);

const CcIcon = () => (
  <span aria-hidden="true" style={{ width: 20, height: 16, borderRadius: 4, border: '2px solid #0065F8', color: '#0065F8', fontSize: 8.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    CC
  </span>
);

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const Spinner = () => (
  <svg className="pxl-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="rgba(0,101,248,0.25)" strokeWidth="3" />
    <path d="M21 12a9 9 0 00-9-9" stroke="#0065F8" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export default function LanguageButton({ audio, onSelectAudio, dubs = null, onDubReady, subtitles = null, className = '' }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('main'); // main | audio | subs | record
  const [maxH, setMaxH] = useState(460);
  const ref = useRef(null);

  // The player clips its overflow, so the menu may only use the room between
  // the button and the top of the player; it scrolls inside that.
  useEffect(() => {
    if (!open || !ref.current) return;
    const top = ref.current.getBoundingClientRect().top;
    let clipTop = 0;
    for (let el = ref.current.parentElement; el; el = el.parentElement) {
      const o = window.getComputedStyle(el).overflow;
      if (o && o !== 'visible') {
        clipTop = el.getBoundingClientRect().top;
        break;
      }
    }
    setMaxH(Math.max(180, Math.min(460, top - clipTop - 18)));
  }, [open]);
  const makingSince = useRef(null);
  const [now, setNow] = useState(Date.now());
  const [flash, setFlash] = useState(null); // language that just became ready
  const prevMaking = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const tracks = audio?.tracks || [];
  const original = dubs?.original_language || '';
  const inStream = new Set(tracks.map((t) => t.lang).filter(Boolean));
  const dubList = dubs?.languages || [];
  const newlyReady = dubList.filter((d) => d.status === 'ready' && !inStream.has(d.code));
  const makeable = dubList.filter((d) => d.status !== 'ready');
  const hasAudio = tracks.length > 1 || newlyReady.length > 0 || makeable.length > 0;
  const hasSubs = Boolean(subtitles && (subtitles.availableLanguages || []).length);

  // The dub *this viewer* asked for, while it records.
  const mine = dubs?.my_active;
  const makingLang =
    mine && dubList.some((d) => d.code === mine.language_code && (d.status === 'queued' || d.status === 'processing'))
      ? mine.language_code
      : dubs?.requesting || null;

  useEffect(() => {
    if (makingLang) {
      if (!makingSince.current) makingSince.current = Date.now();
      const t = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(t);
    }
    makingSince.current = null;
    return undefined;
  }, [makingLang]);

  // Making -> ready: celebrate on the pill.
  const dubListRef = useRef(dubList);
  dubListRef.current = dubList;
  useEffect(() => {
    const was = prevMaking.current;
    prevMaking.current = makingLang;
    if (was && !makingLang && dubListRef.current.some((d) => d.code === was && d.status === 'ready')) setFlash(was);
  }, [makingLang]);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), READY_FLASH_MS);
    return () => clearTimeout(t);
  }, [flash]);

  if (!hasAudio && !hasSubs) return null;

  const current = tracks.find((t) => t.id === audio?.current) || tracks[0] || { id: undefined, lang: '' };
  const currentCode = current.lang || original;
  const dubbed = Boolean(current.lang) && current.lang !== original;
  const subsOn = hasSubs && subtitles.subtitlesEnabled;
  const subLang = subsOn ? subtitles.selectedLang : null;
  const progress = makingLang ? Math.min(0.92, (now - (makingSince.current || now)) / 1000 / MAKE_SECONDS) : null;

  let label;
  if (makingLang) label = `Making ${languageName(makingLang)}…`;
  else if (flash) label = `Now in ${languageName(flash)}!`;
  else if (hasAudio) label = languageName(currentCode) || 'Original';
  else label = subsOn ? languageName(subLang) : 'Subtitles';

  const pillBlue = dubbed && !makingLang;
  const busyElsewhere = Boolean(mine) && !makingLang;
  const readyToPick = [
    ...(tracks.length ? tracks : [{ id: undefined, lang: '' }]).map((t) => ({ kind: 'track', id: t.id, code: t.lang || original })),
    ...newlyReady.map((d) => ({ kind: 'new', code: d.code })),
  ];

  return (
    <div ref={ref} className={className} style={{ position: 'relative', display: 'inline-flex', fontFamily: "'Fredoka', 'Inter', sans-serif" }}>
      <style>{css}</style>
      <button
        type="button"
        key={flash || 'pill'}
        className={`pxl-pill${flash ? ' pxl-pop' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Language: ${label}${subsOn ? `, subtitles ${languageName(subLang)}` : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setView(hasAudio && hasSubs ? 'main' : hasAudio ? 'audio' : 'subs');
          setOpen((v) => !v);
        }}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          padding: '0 12px 0 6px',
          borderRadius: 999,
          border: pillBlue ? '1.5px solid #0065F8' : '1.5px solid rgba(11,26,51,0.10)',
          background: pillBlue ? '#0065F8' : flash ? '#E6F7EE' : 'rgba(255,255,255,0.92)',
          color: pillBlue ? '#FFFFFF' : '#0B1A33',
          boxShadow: pillBlue ? '0 2px 0 #073B8E' : '0 2px 0 rgba(11,26,51,0.08)',
          fontFamily: 'inherit',
          fontSize: 14,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}
      >
        {flash && SPARKS.map(([c, dx, dy], i) => <span key={i} className="pxl-spark" style={{ background: c, '--dx': dx, '--dy': dy, animationDelay: `${i * 40}ms` }} />)}
        {hasAudio || !hasSubs ? (
          <Badge code={makingLang || flash || currentCode} active={pillBlue} progress={progress} />
        ) : (
          <Badge code="cc" active={false} />
        )}
        <span>{label}</span>
        {subsOn && hasAudio && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              borderRadius: 6,
              background: pillBlue ? 'rgba(255,255,255,0.22)' : '#0B1A33',
              color: '#FFFFFF',
              letterSpacing: '0.04em',
            }}
          >
            CC{subLang && subLang !== currentCode ? ` ${subLang.toUpperCase()}` : ''}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="pxl-menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 10px)',
            right: 0,
            width: 264,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: maxH,
            overflowY: 'auto',
            padding: 6,
            borderRadius: 18,
            background: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255,255,255,0.9)',
            boxShadow: '0 16px 40px rgba(11,26,51,0.22)',
            color: '#0B1A33',
            zIndex: 40,
          }}
        >
          {view === 'main' && (
            <>
              <Row onClick={() => setView('audio')} left={<><SpeakerIcon />Audio</>} right={<>{languageName(currentCode) || 'Original'}<Chevron /></>} />
              <Row onClick={() => setView('subs')} left={<><CcIcon />Subtitles</>} right={<>{subsOn ? languageName(subLang) : 'Off'}<Chevron /></>} />
            </>
          )}

          {view !== 'main' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 6px 8px', borderBottom: '1px solid rgba(11,26,51,0.08)', marginBottom: 4 }}>
              {(view === 'record' || (hasAudio && hasSubs)) && (
                <button
                  type="button"
                  aria-label="Back"
                  className="pxl-row"
                  onClick={() => setView(view === 'record' ? 'audio' : 'main')}
                  style={{ border: 'none', background: 'transparent', color: '#0B1A33', width: 30, height: 30, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <Chevron back />
                </button>
              )}
              <span style={{ fontSize: 15, fontWeight: 700, paddingLeft: view === 'record' || (hasAudio && hasSubs) ? 0 : 6 }}>
                {view === 'audio' ? 'Audio' : view === 'subs' ? 'Subtitles' : 'Ask Pip for a language'}
              </span>
            </div>
          )}

          {view === 'audio' && (
            <>
              {readyToPick.map((item) => {
                const selected = item.kind === 'track' && item.id === current.id;
                const isOriginal = item.kind === 'track' && !tracks.find((t) => t.id === item.id)?.lang;
                return (
                  <Row
                    key={`${item.kind}-${item.code}-${item.id ?? ''}`}
                    selected={selected}
                    onClick={() => {
                      if (item.kind === 'new') onDubReady?.(item.code);
                      else if (item.id !== undefined) onSelectAudio?.(item.id);
                      setOpen(false);
                    }}
                    left={
                      <>
                        {languageName(item.code) || 'Original'}
                        {isOriginal && <span style={{ fontSize: 12, fontWeight: 500, color: '#5A6B85' }}>original</span>}
                        {item.kind === 'new' && <span style={{ fontSize: 11, fontWeight: 700, color: '#1E8E5A', background: '#E6F7EE', padding: '1px 6px', borderRadius: 6 }}>new</span>}
                      </>
                    }
                    right={selected ? <Check /> : null}
                  />
                );
              })}
              {makeable.length > 0 && (
                <div style={{ borderTop: '1px solid rgba(11,26,51,0.08)', marginTop: 4, paddingTop: 4 }}>
                  <Row
                    onClick={() => setView('record')}
                    left={
                      <>
                        <img src="/assets/player/pip_sit.webp" alt="" draggable={false} style={{ height: 26, marginLeft: -2 }} />
                        <span style={{ color: '#0065F8' }}>{makingLang ? `Recording ${languageName(makingLang)}…` : 'Another language'}</span>
                      </>
                    }
                    right={makingLang ? <Spinner /> : <Chevron />}
                  />
                </div>
              )}
            </>
          )}

          {view === 'record' && (
            <>
              <div style={{ fontSize: 13, fontWeight: 500, color: dubs?.error ? '#C53030' : '#5A6B85', lineHeight: 1.35, padding: '2px 12px 8px' }}>
                {dubs?.error
                  ? dubs.error
                  : !dubs?.can_request
                    ? 'Sign in to ask Pip for a new language.'
                    : busyElsewhere
                      ? "Pip's recording one for you. Ask for another when it's done."
                      : 'Pip records it in about a minute. Then it’s free for everyone.'}
              </div>
              {makeable.map((d) => {
                const working = d.status === 'queued' || d.status === 'processing' || dubs?.requesting === d.code;
                const failed = d.status === 'failed';
                const disabled = !working && (failed || !dubs?.can_request || Boolean(mine));
                return (
                  <Row
                    key={`mk-${d.code}`}
                    disabled={disabled}
                    onClick={() => !disabled && !working && dubs?.request(d.code)}
                    left={languageName(d.code)}
                    right={working ? <><Spinner />~1 min</> : failed ? 'try later' : null}
                  />
                );
              })}
            </>
          )}

          {view === 'subs' && (
            <>
              <Row
                selected={!subsOn}
                onClick={() => {
                  subtitles.setSubtitlesEnabled(false);
                  setOpen(false);
                }}
                left="Off"
                right={!subsOn ? <Check /> : null}
              />
              {subtitles.availableLanguages.map((code) => {
                const selected = subsOn && subtitles.selectedLang === code;
                const loading = subtitles.loadingLang === code;
                return (
                  <Row
                    key={`cc-${code}`}
                    selected={selected}
                    disabled={loading}
                    onClick={() => {
                      subtitles.selectLanguage(code);
                      setOpen(false);
                    }}
                    left={languageName(code)}
                    right={loading ? <Spinner /> : selected ? <Check /> : null}
                  />
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
